// One-release cleanup for the response cache written by the retired
// react-query IndexedDB persister. This module deliberately uses the native API
// so the app does not retain an IndexedDB dependency merely to delete old data.

const LEGACY_DB = 'keyval-store';
const LEGACY_STORE = 'keyval';
const LEGACY_KEY = 'shubert-ads-query-cache';
const CLEANUP_MARKER = 'shubert.query-cache-idb-removed.v1';

function markComplete(): void {
  try {
    window.localStorage.setItem(CLEANUP_MARKER, '1');
  } catch {
    // Storage can be unavailable in hardened/private browser modes. Retrying
    // the idempotent deletion on the next load is safe.
  }
}

export async function deleteLegacyPersistedQueryCache(): Promise<void> {
  if (typeof window === 'undefined' || typeof indexedDB === 'undefined') return;
  try {
    if (window.localStorage.getItem(CLEANUP_MARKER) === '1') return;
  } catch {
    // Continue without the optimization.
  }

  await new Promise<void>((resolve) => {
    const request = indexedDB.open(LEGACY_DB);
    let databaseDidNotExist = false;

    request.onupgradeneeded = () => {
      // Opening an absent database solely to delete one key would create new
      // storage. Abort the creation and treat the cleanup as complete.
      databaseDidNotExist = true;
      request.transaction?.abort();
    };
    request.onerror = () => {
      if (databaseDidNotExist || request.error?.name === 'AbortError') markComplete();
      resolve();
    };
    request.onsuccess = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(LEGACY_STORE)) {
        database.close();
        markComplete();
        resolve();
        return;
      }
      const transaction = database.transaction(LEGACY_STORE, 'readwrite');
      transaction.objectStore(LEGACY_STORE).delete(LEGACY_KEY);
      transaction.oncomplete = () => {
        database.close();
        markComplete();
        resolve();
      };
      transaction.onerror = () => {
        database.close();
        resolve();
      };
      transaction.onabort = transaction.onerror;
    };
  });
}
